// ─── What: candle-count safeguard ─────────────────────────────────────────────

// Max candles we ever fetch/render for one series. A 1-minute resolution over a
// multi-month range would otherwise produce hundreds of thousands of candles,
// killing both the browser and the backend.
export const MAX_CANDLES = 1500;

// ─── How it works ─────────────────────────────────────────────────────────────

/**
 * Clamp a requested candle resolution (minutes) up to the finest available
 * resolution that keeps the candle count over `rangeMinutes` within budget.
 * Never returns a finer resolution than requested — only bumps coarser.
 */
export function clampResolutionToBudget(
    rangeMinutes: number,
    requested: number,
    available: number[],
    maxCandles: number = MAX_CANDLES
): number {
    if (!(rangeMinutes > 0) || maxCandles <= 0) return requested;

    // Any resolution >= minRes keeps the count within budget.
    const minRes = rangeMinutes / maxCandles;
    const threshold = Math.max(requested, minRes);

    const sorted = [...available].sort((a, b) => a - b);
    for (const res of sorted) {
        if (res >= threshold) return res;
    }
    // Nothing coarse enough — use the coarsest available.
    return sorted[sorted.length - 1] ?? requested;
}

export const MAX_TOTAL_CANDLES = 12000;

export const MIN_CANDLES_PER_SERIES = 400;

export function candleBudgetForSeries(
    seriesCount: number,
    maxCandles: number = MAX_CANDLES,
    maxTotal: number = MAX_TOTAL_CANDLES,
    minPerSeries: number = MIN_CANDLES_PER_SERIES
): number {
    if (!(seriesCount > 1)) return maxCandles;
    return Math.max(
        Math.min(minPerSeries, maxCandles),
        Math.min(maxCandles, Math.floor(maxTotal / seriesCount))
    );
}

export function bestResolutionForBudget(
    rangeMinutes: number,
    available: number[],
    maxCandles: number = MAX_CANDLES
): number {
    const sorted = [...available].sort((a, b) => a - b);
    const finest = sorted[0];
    if (finest === undefined) return MAX_CANDLES;
    return clampResolutionToBudget(rangeMinutes, finest, sorted, maxCandles);
}
